import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { columnMap } from '@/mocks/columns';

type Props = {
  article: Article;
  layout?: 'vertical' | 'horizontal';
  showNumber?: number;
};

export default function ArticleCard({ article, layout = 'vertical', showNumber }: Props) {
  const col = columnMap[article.column];

  if (layout === 'horizontal') {
    return (
      <article className="flex flex-col gap-4 rounded-lg border border-background-200/70 bg-background-50 p-4 transition-colors hover:border-background-300 sm:flex-row md:p-5">
        <Link
          to={`/wenzhang/${article.slug}`}
          className="block w-full shrink-0 overflow-hidden rounded-md sm:w-56"
          aria-label={article.title}
        >
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 看料社深扒与专栏分析`}
            className="h-44 w-full object-cover object-top transition-transform duration-500 hover:scale-105 sm:h-36"
          />
        </Link>
        <div className="flex flex-1 flex-col">
          <div className="mb-2 flex items-center gap-2 text-xs">
            <Link
              to={`/lanmu/${article.column}`}
              className="rounded-sm bg-primary-100 px-2 py-0.5 font-medium text-primary-700"
            >
              {col?.name}
            </Link>
            <span className="text-foreground-500">{article.date}</span>
            <span className="text-foreground-400">·</span>
            <span className="text-foreground-500">{article.readTime}</span>
          </div>
          <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
            <Link to={`/wenzhang/${article.slug}`} className="transition-colors hover:text-primary-700">
              {article.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.lead}</p>
          <div className="mt-3 flex items-center justify-between text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-pencil-line text-sm" aria-hidden="true" />
              编辑 · {article.author}
            </span>
            <Link
              to={`/wenzhang/${article.slug}`}
              className="font-medium text-primary-600 hover:text-primary-800"
            >
              读全文
              <i className="ri-arrow-right-line ml-1 text-sm" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors hover:border-primary-300">
      {typeof showNumber === 'number' && (
        <span className="pointer-events-none absolute z-10 -mt-1 ml-3 flex h-9 min-w-9 items-center justify-center rounded-b-md bg-primary-600 px-2 font-heading text-base font-bold text-background-50">
          {String(showNumber + 1).padStart(2, '0')}
        </span>
      )}
      <Link to={`/wenzhang/${article.slug}`} className="relative block overflow-hidden" aria-label={article.title}>
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 看料社专栏分析`}
          className="h-44 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
          <Link
            to={`/lanmu/${article.column}`}
            className="rounded-sm bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900"
          >
            {col?.name}
          </Link>
          <span className="text-foreground-500">{article.readTime}</span>
          <span className="text-foreground-400">·</span>
          <span className="text-foreground-500">{article.date}</span>
        </div>
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <Link to={`/wenzhang/${article.slug}`} className="transition-colors hover:text-primary-700">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{article.lead}</p>
        <div className="mt-3 flex items-center justify-between border-t border-background-200/70 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-pencil-line text-sm" aria-hidden="true" />
            {article.author}
          </span>
          <Link
            to={`/wenzhang/${article.slug}`}
            className="font-medium text-primary-600 transition-colors hover:text-primary-800"
          >
            读全文
            <i className="ri-arrow-right-line ml-1 text-sm" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </article>
  );
}